import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leaveService, userService } from '../services/api.service';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AppLayout from '../layouts/AppLayout';
import { useAuth } from '../contexts/AuthContext';

const UserLeaveHistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [userData, setUserData] = useState(null);
  
  // Fetch user data and leave history
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Use the correct method: getEmployeeLeaveRequests instead of getUserLeaveRequests
        const [userResponse, leaveHistoryResponse] = await Promise.all([
          userService.getUserById(id),
          leaveService.getEmployeeLeaveRequests(id) // Change this line
        ]);
        
        setUserData(userResponse.user);
        setLeaveHistory(leaveHistoryResponse.leaveRequests || []);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load user leave history');
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get status badge style
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      adjustment: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    return styles[status] || 'bg-gray-100 text-gray-800';
  };
  
  // Calculate total days taken
  const calculateTotalDaysTaken = () => {
    return leaveHistory
      .filter(leave => leave.status === 'approved')
      .reduce((total, leave) => total + (leave.working_days || 0), 0);
  };
  
  // Calculate adjustments total
  const calculateAdjustments = () => {
    return leaveHistory
      .filter(leave => leave.status === 'adjustment')
      .reduce((total, leave) => total + (leave.adjustment_amount || 0), 0);
  };
  
  if (isLoading) {
    return (
      <AppLayout>
        <div className="text-center py-10">Loading leave history...</div>
      </AppLayout>
    );
  }
  
  // Check if current user has permission to view this data
  const hasPermission = currentUser.role === 'admin' || 
                        currentUser.id === id || 
                        (currentUser.role === 'manager' && userData?.department === currentUser.department);
                        
  if (!hasPermission) {
    return (
      <AppLayout>
        <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">
          You don't have permission to view this page
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Leave History: {userData?.name}</h1>
          
          <div className="space-x-2">
            <Button
              variant="outline"
              onClick={() => navigate('/staff')}
            >
              Back to Staff List
            </Button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">
            {error}
          </div>
        )}
        
        {/* Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle>Leave Summary</CardTitle>
          </CardHeader>
          
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-md text-center">
                <h3 className="text-sm text-gray-500 mb-1">Current Balance</h3>
                <p className="text-3xl font-bold">{userData?.leave_balance || 0} days</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-md text-center">
                <h3 className="text-sm text-gray-500 mb-1">Approved Leaves</h3>
                <p className="text-3xl font-bold">
                  {leaveHistory.filter(leave => leave.status === 'approved').length}
                </p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-md text-center">
                <h3 className="text-sm text-gray-500 mb-1">Pending Requests</h3>
                <p className="text-3xl font-bold">
                  {leaveHistory.filter(leave => leave.status === 'pending').length}
                </p>
              </div>
            </div>
            
            {/* New balance details section */}
            <div className="mt-6 border-t pt-4">
              <h3 className="font-medium text-lg mb-3">Leave Balance Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Days Taken (Approved):</span>
                    <span className="font-semibold">{calculateTotalDaysTaken()} days</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Balance Adjustments:</span>
                    <span className="font-semibold">{calculateAdjustments()} days</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-gray-600">Current Balance:</span>
                    <span className="font-bold text-lg">{userData?.leave_balance || 0} days</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="font-medium mb-2">Leave Usage</h4>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                    {userData?.leave_balance > 0 && (
                      <div 
                        className="h-full bg-blue-500" 
                        style={{ 
                          width: `${Math.min(100, calculateTotalDaysTaken() / (calculateTotalDaysTaken() + userData.leave_balance) * 100)}%` 
                        }}
                      ></div>
                    )}
                  </div>
                  <div className="flex justify-between mt-2 text-sm">
                    <span>Used: {calculateTotalDaysTaken()} days</span>
                    <span>Available: {userData?.leave_balance || 0} days</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Leave History</CardTitle>
          </CardHeader>
          
          <CardContent>
            {leaveHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Period</th>
                      <th className="text-left py-3 px-4">Type</th>
                      <th className="text-left py-3 px-4">Days</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Reason</th>
                      <th className="text-left py-3 px-4">Reviewed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveHistory.map(leave => (
                      <tr key={leave.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                        </td>
                        <td className="py-3 px-4">{leave.leave_type_name || 'Standard Leave'}</td>
                        <td className="py-3 px-4">
                          {leave.status === 'adjustment' 
                            ? `${leave.adjustment_amount > 0 ? '+' : ''}${leave.adjustment_amount}` 
                            : `${leave.working_days || '-'}`} days
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(leave.status)} capitalize`}>
                            {leave.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {leave.reason || '-'}
                        </td>
                        <td className="py-3 px-4">
                          {leave.reviewer_name || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No leave history found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default UserLeaveHistoryPage;